import _ from "lodash";
import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import classes from "./table.scss";
// needed for table body level scope DnD setup
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";

import {
  AddColumnOutlined,
  AddOutlined,
  AddRowOutlined,
  SettingsOutlined,
} from "@toddle-edu/ds-icons";
import { Button, ChecklistDropdown, IconButton } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { FIELD_UID_TABLE } from "Worksheet/modules/WorksheetConstants";
import {
  getFixedRowsAndColumnsMemoized,
  getTableSettingsOptions,
} from "./utils";
import DraggableRow from "./DraggableRow";
import PropTypes from "prop-types";
import {
  TABLE_INPUT_END_CHAR,
  TABLE_INPUT_START_CHAR,
  TABLE_STANDARD_TEMPLATE,
} from "./constants";
import ColumnOptionsOverlay from "./ColumnOptionsOverlay";
import RowOptionsOverlay from "./RowOptionsOverlay";
import { useI18n } from "Hooks";

/* 
Different Field Mode
  - create -> Teacher create worksheet, all cells editable, drag and drop enabled, Table expandable.
  - edit -> Student attempt, only answer cells are editable.
  - teacher-preview -> Student attempt (teacher preview), only answer cells are editable (local state change)
  - restricted-view -> Teacher library/ assessment mode, correct answers are not visible.(can't edit)
  - preview -> Teacher library/ assessment mode, correct answers are visible.(can't edit)
  - evaluate -> Student and Teacher can see responses and correct answers.(can't edit)
  - view -> Student responses are visible to teacher and student. (can't edit)
 */

const Table = props => {
  const {
    mode,
    value,
    updateTableData,
    additionalClassesTable,
    suffixElementTable,
    maxColumnSize,
    maxRowSize,
    expandTableButtonsInFooter,
    showTableDetailsAndHeaderSettings,
    customCellHeaderStyles,
    showDisabledCellClasses,
    shouldHighlightInputText,
    customTableClasses,
    additionalCellStyles,
    testId,
    shouldRestrictDnDToParent,
    scrollContainerStyles,
    customActiveCellStyles,
  } = props;
  const { t } = useI18n();
  const settingsConfig = getTableSettingsOptions({ t });
  const scrollContainerRef = useRef(null);
  // Ref tracking latest table state so setCellValue doesn't need to close over
  // data/columns/columnOrder, which would make it unstable on every data change.
  const tableStateRef = useRef({
    data: [],
    columns: [],
    columnOrder: [],
    headerType: [],
  });
  const updateTableDataRef = useRef(updateTableData);

  const [columns, setColumns] = useState([
    {
      accessorKey: `col1`,
      cell: info => info.getValue(),
      header: "COL 1",
      id: "1",
    },
    {
      accessorKey: `col2`,
      cell: info => info.getValue(),
      header: "COL 2",
      id: "2",
    },
    {
      accessorKey: `col3`,
      header: "COL 3",
      cell: info => info.getValue(),
      id: "3",
    },
  ]);

  const [data, setData] = useState(() =>
    _.isEmpty(_.get(value, [FIELD_UID_TABLE, "data"]))
      ? _.cloneDeep(TABLE_STANDARD_TEMPLATE)
      : _.get(value, [FIELD_UID_TABLE, "data"], {})
  );

  const [columnOrder, setColumnOrder] = useState(() =>
    columns.map(c => _.get(c, "id"))
  );
  const [headerType, setHeaderType] = useState([]);

  // Keep tableStateRef and updateTableDataRef in sync so setCellValue can read
  // the latest values without listing them as useCallback deps (which would
  // make setCellValue unstable and trigger doc-editor re-mounts on every change).
  tableStateRef.current = { data, columns, columnOrder, headerType };
  updateTableDataRef.current = updateTableData;

  // showColumnOptions is integer, will have column id, and will show dropdown near the column with same id as here
  // , -1 means no active column, no dropdown shown
  const [showColumnOptions, setShowColumnOptions] = useState(-1);
  // This will hold the positon of the column
  const [columnOptionsPosition, setColumnOptionsPosition] = useState({});
  // showRowOptions is integer, will have row id, and will show dropdown near the row with same id as here
  // , -1 means no active rows, no dropdown shown
  const [showRowOptions, setShowRowOptions] = useState(-1);
  // This will hold the positon of the row
  const [rowOptionsPosition, setRowOptionsPosition] = useState({});
  const setTableTemplateData = () => {
    updateTableData({
      headerType: headerType,
      columns: columns,
      columnOrder: columnOrder,
      data: TABLE_STANDARD_TEMPLATE,
    });
  };
  useEffect(() => {
    if (showColumnOptions > -1) setShowRowOptions(-1);
  }, [showColumnOptions]);
  useEffect(() => {
    if (showRowOptions > -1) setShowColumnOptions(-1);
  }, [showRowOptions]);

  useEffect(() => {
    const tableData = _.get(value, FIELD_UID_TABLE, {});

    const { columns, data, headerType } = tableData || {};
    if (_.isNil(data)) setTableTemplateData();
    if (!_.isEmpty(columns)) {
      setColumns(columns);
      setColumnOrder(() => columns.map(c => _.get(c, "id")));
    }

    if (!_.isEmpty(data)) {
      setData(data);
    }

    if (!_.isEmpty(headerType)) {
      setHeaderType(headerType);
    }
  }, [value]);

  const dataIds = useMemo(
    () =>
      data?.map(({ id }) => {
        return id;
      }),
    [data]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => _.get(row, "id"), //required because row indexes will change
    state: {
      columnOrder,
    },
    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      size: 200, //starting column size
      minSize: 50, //enforced during column resizing
      maxSize: "100%", //enforced during column resizing
    },
  });

  useEffect(() => {
    table.options.dragHandleRefsCol = {};
  }, [table]);

  // reorder columns after drag & drop
  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;
      // for columns
      if (active && over && _.get(active, "id") !== _.get(over, "id")) {
        const oldIndex = columnOrder.indexOf(_.get(active, "id"));
        const newIndex = columnOrder.indexOf(_.get(over, "id"));
        if (!_.isEqual(oldIndex, -1) && !_.isEqual(newIndex, -1)) {
          setColumnOrder(
            arrayMove(columnOrder, oldIndex, newIndex) //this is just a splice util
          );
          updateTableData({
            headerType: headerType,
            columns: columns,
            columnOrder: arrayMove(columnOrder, oldIndex, newIndex),
            data: data,
          });
        }
      }
      // for rows
      if (active && over && _.get(active, "id") !== _.get(over, "id")) {
        const oldIndex = dataIds.indexOf(_.get(active, "id"));
        const newIndex = dataIds.indexOf(_.get(over, "id"));

        if (!_.isEqual(oldIndex, -1) && !_.isEqual(newIndex, -1)) {
          setData(data => {
            return arrayMove(data, oldIndex, newIndex); //this is just a splice util
          });

          updateTableData({
            headerType: headerType,
            columns: columns,
            columnOrder: columnOrder,
            data: arrayMove(data, oldIndex, newIndex),
          });
        }
      }
    },
    [data, columnOrder, columns, headerType, dataIds, updateTableData]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );
  const modifiers = shouldRestrictDnDToParent ? [restrictToParentElement] : [];

  const addCol = colIndex => {
    if (maxColumnSize && _.size(columns) > maxColumnSize - 1) return;
    let maxId = _.size(columns) + 1;
    _.forEach(columnOrder, col => {
      if (maxId <= col) maxId = parseInt(col) + 1;
    });
    const columnId = _.toString(maxId);
    // Create the new column
    const newColumn = {
      accessorKey: `col${maxId}`,
      cell: info => info.getValue(),
      header: `COL ${maxId}`,
      id: columnId,
    };

    // Insert the new column at the specified index
    const newCols = [
      ...columns.slice(0, colIndex),
      newColumn,
      ...columns.slice(colIndex),
    ];
    // Update columnOrder
    const newColumnOrder = [
      ...columnOrder.slice(0, colIndex),
      columnId,
      ...columnOrder.slice(colIndex),
    ];

    setColumns(newCols);
    setColumnOrder(newColumnOrder);

    // Insert new column data at the specified index for each row
    const newData = _.cloneDeep(data);
    _.forEach(newData, row => (row[`col${maxId}`] = ""));
    setData(newData);

    // closing column options if it is open
    setShowColumnOptions(-1);

    // Update the table question data
    updateTableData({
      headerType: headerType,
      columns: newCols,
      columnOrder: newColumnOrder,
      data: newData,
    });

    // auto scroll to newly added column
    setTimeout(() => {
      const newColumnDragHandleRef =
        table.options.dragHandleRefsCol[columnId]?.current;

      if (newColumnDragHandleRef) {
        const offsetLeft = newColumnDragHandleRef.offsetLeft;
        // as the handle is always on the middle of the column, offsetLeft*2 is the width of the column
        const scrollPosition = offsetLeft * 2 * (colIndex - 1);
        scrollContainerRef?.current.scrollTo({
          left: scrollPosition,
        });
      }
    }, 100);
  };

  const deleteCol = id => {
    const column = _.find(columns, col => col.id === id);

    if (!column || _.size(columns) < 2) return;
    const newCols = _.filter(columns, col => col.id !== id);
    const newColumnOrder = _.filter(columnOrder, colId => colId !== id);

    // // Remove the corresponding key from each row in data
    const columnToDelete = column.accessorKey;
    const newData = _.map(data, row => {
      const { [columnToDelete]: _, ...remainingData } = row;
      return remainingData;
    });

    setColumns(newCols);
    setColumnOrder(newColumnOrder);
    setData(newData);

    // close the columnOptions
    setShowColumnOptions(-1);

    // Update the table question data
    updateTableData({
      headerType: headerType,
      columns: newCols,
      columnOrder: newColumnOrder,
      data: newData,
    });
  };

  const addRow = rowIndex => {
    if (maxRowSize && _.size(data) > maxRowSize - 1) return;

    // Create the new row
    const newRow = { id: _.size(data) };
    _.forEach(columns, col => {
      newRow[_.get(col, "accessorKey")] = "";
    });

    // Insert the new row at the specified index
    const newData = [
      ...data.slice(0, rowIndex),
      newRow,
      ...data.slice(rowIndex),
    ];
    // Reassign IDs to maintain uniqueness
    const updatedData = newData.map((row, index) => ({
      ...row,
      id: index + 1, // Reassigning IDs based on index
    }));
    setData(updatedData);

    // Update the table question data
    updateTableData({
      headerType: headerType,
      columns: columns,
      columnOrder: columnOrder,
      data: updatedData,
    });

    // Closing row options if it is open
    setShowRowOptions(-1);
  };

  const deleteRow = rowId => {
    const newData = _.filter(data, row => row.id !== rowId);
    if (_.size(newData) === _.size(data) || _.size(data) < 2) return;
    const updatedData = newData.map((row, index) => ({
      ...row,
      id: index + 1, // Reassigning IDs based on index
    }));
    setData(updatedData);

    // Update the table question data
    updateTableData({
      headerType: headerType,
      columns: columns,
      columnOrder: columnOrder,
      data: updatedData,
    });

    // Close the row options if it is open
    setShowRowOptions(-1);
  };

  const setCellValue = useCallback(({ rowId, colId, val, isInputCell }) => {
    const {
      data: currentData,
      columns,
      columnOrder,
      headerType,
    } = tableStateRef.current;

    const rowIndex = currentData.findIndex(row => row.id === rowId);
    if (rowIndex === -1) return;

    const updatedRow = { ...currentData[rowIndex] };
    updatedRow[colId] = isInputCell
      ? `${TABLE_INPUT_START_CHAR}${val}${TABLE_INPUT_END_CHAR}`
      : val;

    const updatedData = [
      ...currentData.slice(0, rowIndex),
      updatedRow,
      ...currentData.slice(rowIndex + 1),
    ];

    setData(updatedData);
    updateTableDataRef.current({
      headerType,
      columnOrder,
      columns,
      data: updatedData,
    });
  }, []);

  const onHeaderChange = useCallback(
    e => {
      setHeaderType(e);
      updateTableData({
        headerType: e,
        data: data,
        columns: columns,
        columnOrder: columnOrder,
      });
    },
    [data, columns, columnOrder, updateTableData]
  );

  // Params used to show active column, when show column options overlay is rendered.
  const getTableActivePrarms = useCallback(() => {
    return {
      // getting one column left to the active column
      leftOfActiveCol: _.get(
        columnOrder,
        _.indexOf(columnOrder, showColumnOptions) - 1
      ),
      // getting last row of the table
      bottomRowIndex: _.get(_.last(data), "id"),
    };
  }, [columnOrder, showColumnOptions, data]);

  const tableDescriptionClass = classNames(
    "text-body-3",
    classes.tableDescription
  );
  const tableHeadingClass = classNames("text-label-2", classes.tableHeading);
  const tableClasses = classNames(customTableClasses, classes.table);

  const hasColHeader = _.indexOf(headerType, "COL") !== -1;
  const hasRowHeader = _.indexOf(headerType, "ROW") !== -1;
  const isCreateMode = _.isEqual(mode, "create");
  const expandTableButtonsOnRowsAndColumns =
    isCreateMode && !expandTableButtonsInFooter;
  const showTableAddButtonOnFooter = isCreateMode && expandTableButtonsInFooter;

  const tableWrapperClasses = classNames({
    [classes.wrapper]: true,
    [classes.wrapperCreateMode]: isCreateMode,
  });

  // Function to filter out the rows and columns which are fixed
  // so that while dragging other items, fixed items don't shift.
  const customClosestCenterCollision = args => {
    const pointerIntersections = closestCenter(args);

    const fixedRowsAndColumns = getFixedRowsAndColumnsMemoized({
      rows: data,
      columns,
    });
    return pointerIntersections.filter(intersection => {
      return !_.find(fixedRowsAndColumns, { id: intersection.id });
    });
  };

  return (
    <div className={classes.mainContainer}>
      <div
        className={classes.tableAndTableDetailsContainer}
        ref={scrollContainerRef}
        style={scrollContainerStyles}
      >
        {isCreateMode && showTableDetailsAndHeaderSettings ? (
          <div className={classes.container}>
            <div className={classes.settingsWrapper}>
              <div className={tableHeadingClass}>{t("worksheet:table")}</div>
              <ChecklistDropdown
                options={settingsConfig}
                value={headerType}
                onChange={onHeaderChange}
              >
                <IconButton
                  icon={<SettingsOutlined variant="subtle" />}
                  dsVersion="2.0"
                  _legacy_variant="outlined-subtle"
                  variant="neutral"
                  type="outlined"
                  testId={`${testId}-table-changeHeader`}
                ></IconButton>
              </ChecklistDropdown>
            </div>
            <div className={tableDescriptionClass}>
              {t("worksheet:table_question_description")}
            </div>
          </div>
        ) : null}
        <div className={tableWrapperClasses}>
          <div className={classes.tableContainer}>
            <table className={tableClasses}>
              <DndContext
                collisionDetection={customClosestCenterCollision}
                onDragEnd={handleDragEnd}
                sensors={sensors}
                modifiers={modifiers}
              >
                <tbody className={classes.tbody}>
                  <SortableContext items={dataIds}>
                    {table.getRowModel().rows.map((row, rowIndex) => {
                      const additionalClassesRow = _.find(
                        additionalClassesTable,
                        classesRow => _.isEqual(_.get(classesRow, "id"), row.id)
                      );
                      const additionalCellStylesRow = _.find(
                        additionalCellStyles,
                        classesRow => _.isEqual(_.get(classesRow, "id"), row.id)
                      );
                      const suffixElementRow = _.find(
                        suffixElementTable,
                        classesRow => _.isEqual(_.get(classesRow, "id"), row.id)
                      );
                      return (
                        <DraggableRow
                          setCellValue={setCellValue}
                          mode={mode}
                          key={row.id}
                          row={row}
                          columnOrder={columnOrder}
                          hasColHeader={hasColHeader}
                          hasRowHeader={hasRowHeader}
                          rowIndex={rowIndex}
                          additionalClassesRow={additionalClassesRow}
                          suffixElementRow={suffixElementRow}
                          setShowColumnOptions={setShowColumnOptions}
                          showColumnOptions={showColumnOptions}
                          setColumnOptionsPosition={setColumnOptionsPosition}
                          setShowRowOptions={setShowRowOptions}
                          showRowOptions={showRowOptions}
                          setRowOptionsPosition={setRowOptionsPosition}
                          getTableActivePrarms={getTableActivePrarms}
                          customCellHeaderStyles={customCellHeaderStyles}
                          showDisabledCellClasses={showDisabledCellClasses}
                          shouldHighlightInputText={shouldHighlightInputText}
                          additionalCellStylesRow={additionalCellStylesRow}
                          testId={testId}
                          lastRowIndex={_.size(data) - 1}
                          customActiveCellStyles={customActiveCellStyles}
                        />
                      );
                    })}
                  </SortableContext>
                </tbody>
              </DndContext>
            </table>
          </div>
          {expandTableButtonsOnRowsAndColumns ? (
            <span className={classes.addColWrapper}>
              <IconButton
                className={classes.addColBut}
                onClick={() => addCol(_.size(columns))}
                dsVersion="2.0"
                _legacy_variant="outlined-subtle"
                variant="neutral"
                type="outlined"
                icon={<AddOutlined variant="subtle" />}
                testId={`${testId}-table-addColumn`}
                disabled={maxColumnSize && _.size(columns) > maxColumnSize - 1}
              />
            </span>
          ) : null}
        </div>
        {expandTableButtonsOnRowsAndColumns ? (
          <IconButton
            className={classes.addRowBut}
            onClick={() => addRow(_.size(data))}
            dsVersion="2.0"
            _legacy_variant="outlined-subtle"
            variant="neutral"
            type="outlined"
            icon={<AddOutlined variant="subtle" />}
            testId={`${testId}-table-addRow`}
            disabled={maxRowSize && _.size(data) > maxRowSize - 1}
          />
        ) : null}

        {/* Column Options will be visible when drag handle is clicked */}
        {showColumnOptions > -1 && !_.isEmpty(columnOptionsPosition) && (
          <span
            className={classes.columnOptionsContainer}
            style={{
              position: "fixed",
              top: _.get(columnOptionsPosition, "top") + 10,
              left: _.get(columnOptionsPosition, "left") - 50,
            }}
          >
            <ColumnOptionsOverlay
              deleteCol={deleteCol}
              index={_.get(columnOptionsPosition, "colIndex")}
              id={_.get(columnOptionsPosition, "columId")}
              addCol={addCol}
              addColumnDisabled={
                maxColumnSize && _.size(columns) > maxColumnSize - 1
              }
              t={t}
              testId={testId}
              deleteColumnDisabled={_.size(columns) < 2}
            />
          </span>
        )}
        {/* Row Options will be visible when drag handle is clicked */}
        {showRowOptions > -1 && !_.isEmpty(rowOptionsPosition) && (
          <span
            className={classes.rowOptionsContainer}
            style={{
              position: "fixed",
              top:
                _.get(rowOptionsPosition, "top") +
                _.get(rowOptionsPosition, "height") +
                5,
              left: _.get(rowOptionsPosition, "left"),
            }}
          >
            <RowOptionsOverlay
              deleteRow={deleteRow}
              index={_.get(rowOptionsPosition, "rowIndex")}
              id={_.get(rowOptionsPosition, "rowId")}
              addRow={addRow}
              addRowDisabled={maxRowSize && _.size(data) > maxRowSize - 1}
              t={t}
              testId={testId}
            />
          </span>
        )}
      </div>
      {showTableAddButtonOnFooter ? (
        <div className={classes.footerButtonContainer}>
          <Button
            variant="outlined-subtle"
            size="medium"
            onClick={() => addRow(_.size(data))}
            icon={<AddRowOutlined />}
            testId={`${testId}-table-addRow-footer-button`}
          >
            {t("worksheet:add_row")}
          </Button>
          <Button
            variant="outlined-subtle"
            onClick={() => addCol(_.size(columns))}
            size="medium"
            icon={<AddColumnOutlined />}
            testId={`${testId}-table-addColumn-footer-button`}
          >
            {t("worksheet:add_column")}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

Table.displayName = "Table";

Table.defaultProps = {
  mode: "create",
  value: {},
  updateTableData: () => {},
  additionalClassesTable: {},
  suffixElementTable: {},
  maxColumnSize: undefined,
  maxRowSize: undefined,
  expandTableButtonsInFooter: false,
  showTableDetailsAndHeaderSettings: true,
  customCellHeaderStyles: {},
  showDisabledCellClasses: true,
  shouldHighlightInputText: false,
  customTableClasses: {},
  additionalCellStyles: {},
  testId: "UiComponent",
  shouldRestrictDnDToParent: false,
  scrollContainerStyles: {},
  customActiveCellStyles: {},
};

Table.propTypes = {
  mode: PropTypes.string,
  value: PropTypes.object,
  updateTableData: PropTypes.func,
  additionalClassesTable: PropTypes.object,
  suffixElementTable: PropTypes.object,
  maxColumnSize: [PropTypes.number, PropTypes.undefined],
  maxRowSize: [PropTypes.number, PropTypes.undefined],
  expandTableButtonsInFooter: PropTypes.bool,
  showTableDetailsAndHeaderSettings: PropTypes.bool,
  customCellHeaderStyles: PropTypes.object,
  showDisabledCellClasses: PropTypes.bool,
  shouldHighlightInputText: PropTypes.bool,
  customTableClasses: PropTypes.object,
  additionalCellStyles: PropTypes.object,
  testId: PropTypes.string,
  shouldRestrictDnDToParent: PropTypes.bool,
  scrollContainerStyles: PropTypes.object,
  customActiveCellStyles: PropTypes.object,
};
export default Table;
